import { isAxiosError } from "axios";

export interface ApiErrorInfo {
  /** Human-readable message safe to show the user, or null when unknown. */
  message: string | null;
  /** Lambda error code (e.g. "seat_limit_reached"), when the body carried one. */
  code: string | null;
  /** HTTP status, when a response was received. */
  status: number | null;
  /** Lambda error details payload (e.g. { seat_limit, seats_used }). */
  details: Record<string, unknown> | null;
}

const SESSION_EXPIRED_MESSAGE =
  "Your session has expired or lost access. Sign in again; contact your administrator if it keeps happening.";
const TIMEOUT_MESSAGE =
  "The request timed out. Refresh to check what went through before retrying.";
const NETWORK_MESSAGE =
  "Couldn't reach the server. Check your connection and try again.";

/**
 * Normalizes the three error-body shapes this app actually receives:
 * - the profile API's lambda errors: { error, code, details? }
 * - API-Gateway/authorizer-generated errors: { message } (401/403/5xx)
 * - no response at all: timeouts and network failures
 * Callers show `message ?? <their own fallback>` — never raw bodies.
 */
export function extractApiError(error: unknown): ApiErrorInfo {
  if (!isAxiosError(error)) {
    return { message: null, code: null, status: null, details: null };
  }

  const response = error.response;
  if (!response) {
    // A deliberately aborted request (unmount, superseded query) is not a
    // connectivity problem — don't tell the user to check their network.
    if (error.code === "ERR_CANCELED") {
      return { message: null, code: "canceled", status: null, details: null };
    }
    const timedOut =
      error.code === "ECONNABORTED" || error.code === "ETIMEDOUT";
    return {
      message: timedOut ? TIMEOUT_MESSAGE : NETWORK_MESSAGE,
      code: timedOut ? "timeout" : "network",
      status: null,
      details: null,
    };
  }

  const data = response.data as
    | { error?: unknown; code?: unknown; details?: unknown; message?: unknown }
    | undefined;
  const bodyError = typeof data?.error === "string" ? data.error : null;
  const code = typeof data?.code === "string" ? data.code : null;
  const details =
    data?.details && typeof data.details === "object"
      ? (data.details as Record<string, unknown>)
      : null;

  if (bodyError) {
    return { message: bodyError, code, status: response.status, details };
  }

  // Gateway/authorizer bodies ({"message":"Forbidden"} etc.) aren't written
  // for end users — translate the auth ones, drop the rest.
  if (response.status === 401 || response.status === 403) {
    return {
      message: SESSION_EXPIRED_MESSAGE,
      code: "access_denied",
      status: response.status,
      details: null,
    };
  }

  return { message: null, code, status: response.status, details: null };
}

/** Best displayable message for an API failure, else the caller's fallback. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  return extractApiError(error).message ?? fallback;
}
